import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from '../../util/reactIntl';

import css from './VirtualTourPlayer.module.css';

const fullscreenElement = () => document.fullscreenElement || document.webkitFullscreenElement;

// Only exit fullscreen owned by this player, never another listing control.
const exitNativeFullscreen = element => {
  if (fullscreenElement() !== element) return;
  const exit = document.exitFullscreen || document.webkitExitFullscreen;
  try {
    const result = exit?.call(document);
    result?.catch(() => {});
  } catch (error) {
    // The in-page close control still works when the browser rejects the API.
  }
};

const VirtualTourPlayer = ({ src }) => {
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const expandedRef = useRef(false);
  const nativeFullscreenRef = useRef(false);
  const scrollPositionRef = useRef({ scrollX: 0, scrollY: 0 });
  const [expanded, setExpanded] = useState(false);

  const close = useCallback(() => {
    expandedRef.current = false;
    setExpanded(false);
    exitNativeFullscreen(containerRef.current);
  }, []);

  const open = () => {
    scrollPositionRef.current = { scrollX: window.scrollX, scrollY: window.scrollY };
    expandedRef.current = true;
    setExpanded(true);

    const container = containerRef.current;
    const request = container.requestFullscreen || container.webkitRequestFullscreen;
    try {
      // Keep this in the click gesture. iPhone/unsupported browsers use the
      // fixed in-page container, including when a fullscreen request rejects.
      const result = request?.call(container);
      result?.catch(() => {});
    } catch (error) {
      // Full-window mode is already active; never navigate to the provider.
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    const handleFullscreenChange = () => {
      if (fullscreenElement() === container) {
        nativeFullscreenRef.current = true;
        // A pending request can finish after Close was clicked.
        if (!expandedRef.current) exitNativeFullscreen(container);
      } else if (nativeFullscreenRef.current) {
        nativeFullscreenRef.current = false;
        expandedRef.current = false;
        setExpanded(false);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      expandedRef.current = false;
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      exitNativeFullscreen(container);
    };
  }, []);

  useEffect(() => {
    if (!expanded) return undefined;

    const container = containerRef.current;
    const previousFocus = document.activeElement;
    const { scrollX, scrollY } = scrollPositionRef.current;
    const body = document.body;
    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    const properties = ['position', 'top', 'left', 'width', 'overflow'];
    const previousBodyStyles = properties.map(property => body.style[property]);

    // Fixed positioning also locks background scrolling in mobile Safari.
    root.style.overflow = 'hidden';
    Object.assign(body.style, {
      position: 'fixed',
      top: `${-scrollY}px`,
      left: `${-scrollX}px`,
      width: '100%',
      overflow: 'hidden',
    });

    const updateHeight = () =>
      container.style.setProperty('--tourViewportHeight', `${window.innerHeight}px`);
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    };
    const keepFocusInPlayer = event => {
      if (!container.contains(event.target)) buttonRef.current?.focus({ preventScroll: true });
    };

    updateHeight();
    buttonRef.current.focus({ preventScroll: true });
    window.addEventListener('resize', updateHeight);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('focusin', keepFocusInPlayer);

    return () => {
      window.removeEventListener('resize', updateHeight);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('focusin', keepFocusInPlayer);
      container.style.removeProperty('--tourViewportHeight');
      root.style.overflow = previousOverflow;
      properties.forEach((property, index) => {
        body.style[property] = previousBodyStyles[index];
      });
      window.scrollTo(scrollX, scrollY);
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [expanded, close]);

  return (
    <div
      ref={containerRef}
      className={expanded ? `${css.root} ${css.expanded}` : css.root}
      role={expanded ? 'dialog' : undefined}
      aria-modal={expanded ? 'true' : undefined}
      aria-label={expanded ? '360° virtual tour' : undefined}
    >
      <div className={css.toolbar}>
        <button
          ref={buttonRef}
          type="button"
          className={css.fullscreenButton}
          onClick={expanded ? close : open}
          aria-expanded={expanded}
        >
          <FormattedMessage
            id={
              expanded
                ? 'ListingPage.virtualTourExitFullscreen'
                : 'ListingPage.virtualTourFullscreen'
            }
          />
        </button>
      </div>
      <iframe
        className={css.iframe}
        src={src}
        title="360° virtual tour"
        loading="lazy"
        frameBorder="0"
        scrolling="no"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen; xr-spatial-tracking"
        allowFullScreen
      />
    </div>
  );
};

export default VirtualTourPlayer;
